/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { setLocation } from 'js/lib/coursera.redirect';
import { get as getUser } from 'js/lib/user';

import { Typography2 } from '@coursera/cds-core';

import {
  type PROGRAM_JOIN_ERRORS,
  PROGRAM_JOIN_CUSTOM_MESSAGE_ERRORS as customErrorMessageCodes,
} from 'bundles/enterprise-legacy-learner-home/constants/ProgramJoinModalErrors';
import { useNaptime } from 'bundles/naptimejs';
import ProgramSwitcherSelectionsV1 from 'bundles/naptimejs/resources/programSwitcherSelections.v1';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import Modal from 'bundles/unified-description-page-common/components/shared/modals/Modal';

import _t from 'i18n!nls/unified-description-page-common';

type Props = {
  programJoinErrorCode: keyof typeof PROGRAM_JOIN_ERRORS;
  isOpen: boolean;
  setOpen: (x: boolean) => void;
};

const styles = {
  customText: css`
    background-color: var(--cds-color-blue-25);
    padding: var(--cds-spacing-200);
    margin-top: var(--cds-spacing-300);
  `,
};

const ProgramAtCapacityModal = ({ isOpen, setOpen, programJoinErrorCode }: Props) => {
  const { naptime } = useNaptime(() => ({}));
  const features = useEnterpriseProductFeatures();
  const [isProgramSwitcherLoading, setIsProgramSwitcherLoading] = React.useState(false);
  const userId = getUser().id;

  const onClickBrowse = () => {
    setIsProgramSwitcherLoading(true);
    naptime
      .executeMutation(
        ProgramSwitcherSelectionsV1.update(userId, {
          selectionType: 'COURSERA',
        })
      )
      .finally(() => {
        setLocation('/browse');
      });
  };

  const customMessageErrorCode = programJoinErrorCode
    ? customErrorMessageCodes[programJoinErrorCode as keyof typeof customErrorMessageCodes]
    : '';
  const customErrorMessage = customMessageErrorCode && features?.filledProgramCustomMessage;

  return (
    <Modal
      setOpen={setOpen}
      open={isOpen}
      header={_t('This learning program is full')}
      onButtonClick={onClickBrowse}
      buttonLabel={_t('Browse Coursera')}
      isButtonLoading={isProgramSwitcherLoading}
    >
      <Typography2 variant="bodyPrimary" component="p">
        {_t('Contact your organization admin to add availability.')}
      </Typography2>
      {!!customErrorMessage && (
        <Typography2 variant="bodySecondary" component="p" css={styles.customText}>
          {customErrorMessage}
        </Typography2>
      )}
    </Modal>
  );
};

export default ProgramAtCapacityModal;
